import { useQuery } from "@tanstack/react-query";
import { STATES } from "@/data/states";
import { ALL_GAMES, MULTISTATE_GAMES, STATE_GAMES, type Game } from "@/data/games";
import { JACKPOT_SNAPSHOTS } from "@/data/jackpots";
import { getResultsForGame, getLatestResults, ALL_RESULTS, generateLatestResultForGame } from "@/data/results";
import { TAX_RATES } from "@/data/tax-rates";
import { getWinnersForMonth } from "@/data/winners";
import { fetchLiveLottery, buildLiveMaps, type LiveByGameMaps } from "@/lib/lottery-api.functions";
import { getGameOverridesCached } from "@/lib/game-overrides-read.functions";

const STALE = 5 * 60 * 1000;

type GameOverrideRow = {
  slug: string;
  name: string | null;
  logo_url: string | null;
  draw_schedule: string | null;
  description: string | null;
  updated_at?: string | null;
};

async function fetchGameOverrides(): Promise<GameOverrideRow[]> {
  try {
    return (await getGameOverridesCached()) as GameOverrideRow[];
  } catch {
    return [];
  }
}

function buildOverrideMap(overrides: GameOverrideRow[] | undefined) {
  return new Map<string, GameOverrideRow>((overrides ?? []).map((o) => [o.slug, o]));
}

function getOverrideVersion(overrides: GameOverrideRow[] | undefined) {
  return (overrides ?? [])
    .map((o) => `${o.slug}:${o.updated_at ?? ""}:${o.name ?? ""}:${o.logo_url ?? ""}:${o.draw_schedule ?? ""}:${o.description ?? ""}`)
    .sort()
    .join("|");
}

function applyGameOverride(g: Game, override?: GameOverrideRow | null): Game {
  if (!override) return g;
  return {
    ...g,
    name: override.name ?? g.name,
    logo_url: override.logo_url ?? g.logo_url,
    draw_schedule: override.draw_schedule ?? g.draw_schedule,
    description: override.description ?? g.description,
  };
}

function attachStates(g: Game) {
  if (g.is_multistate) return { ...g, states: null };
  const st = STATES.find((s) => s.id === g.state_id);
  return { ...g, states: st ? { name: st.name, slug: st.slug, abbreviation: st.abbreviation, official_lottery_url: st.official_lottery_url } : null };
}

/**
 * Per-state exclusion list. Slugs (as they appear after synthesizing from the
 * live API — `${state.slug}-${slugify(name)}`) that should never be surfaced
 * on the state page even if the upstream feed still returns them.
 */
const EXCLUDED_STATE_GAME_SLUGS: Record<string, ReadonlySet<string>> = {
  pennsylvania: new Set([
    "pennsylvania-pick-2-day",
    "pennsylvania-pick-3-day",
    "pennsylvania-pick-4-day",
    "pennsylvania-pick-5-day",
  ]),
};

/** Build a Game shell purely from a game_overrides row (for CMS-only games). */
function gameFromOverride(o: GameOverrideRow, stateId: string): Game {
  return {
    id: `override-${o.slug}`,
    name: o.name ?? o.slug,
    slug: o.slug,
    is_multistate: false,
    state_id: stateId,
    number_count_main: 0,
    number_range_main: 0,
    number_count_bonus: 0,
    number_range_bonus: 0,
    has_multiplier: false,
    ticket_price: 0,
    draw_schedule: o.draw_schedule ?? "See lottery site for schedule",
    description: o.description ?? undefined,
    logo_url: o.logo_url,
  };
}

export function useLiveLottery() {
  return useQuery({
    queryKey: ["live-lottery"],
    queryFn: async () => {
      const data = await fetchLiveLottery();
      return { data, maps: buildLiveMaps(data) };
    },
    staleTime: STALE,
    refetchOnWindowFocus: false,
  });
}

function mergeJackpot(snap: typeof JACKPOT_SNAPSHOTS[number], maps: LiveByGameMaps | undefined) {
  const live = maps?.jackpotByGameId[snap.game_id];
  if (!live) return snap;
  return {
    ...snap,
    jackpot_amount: live.jackpot_amount,
    cash_value: live.cash_value ?? snap.cash_value,
    next_draw_datetime: live.next_draw_iso,
  };
}

function mergeResult<T extends { game_id: string; draw_date: string; winning_numbers: number[]; bonus_numbers: number[]; multiplier: number | null }>(
  r: T,
  maps: LiveByGameMaps | undefined,
): T {
  const live = maps?.latestByGameId[r.game_id];
  if (!live) return r;
  // Only override the latest entry per game
  if (r.draw_date > live.draw_date) return r; // live feed wins on equal dates so real winning numbers show
  return { ...r, draw_date: live.draw_date, winning_numbers: live.winning_numbers, bonus_numbers: live.bonus_numbers, multiplier: live.multiplier };
}

export function useJackpots() {
  const { data: live } = useLiveLottery();
  const { data: overrides } = useQuery({
    queryKey: ["game-overrides"],
    queryFn: fetchGameOverrides,
    staleTime: 60_000,
  });
  const overrideMap = buildOverrideMap(overrides);
  const overrideVersion = getOverrideVersion(overrides);
  return useQuery({
    queryKey: ["jackpots", live?.data.fetched_at ?? "static", overrideVersion],
    queryFn: async () => {
      const base = JACKPOT_SNAPSHOTS.map((j) => ({
        ...mergeJackpot(j, live?.maps),
        games: attachStates(applyGameOverride(j.games as Game, overrideMap.get((j.games as Game).slug))),
      }));
      // Add synthetic games from the live API (any state/multistate game with a jackpot
      // that isn't already in our static set).
      const seen = new Set(base.map((b) => b.game_id));
      const extras = (live?.maps.syntheticGames ?? [])
        .filter((s) => s.jackpot && !seen.has(s.game.id))
        .map((s) => ({
          id: `jp-${s.game.id}`,
          game_id: s.game.id,
          jackpot_amount: s.jackpot!.jackpot_amount,
          cash_value: s.jackpot!.cash_value,
          next_draw_datetime: s.jackpot!.next_draw_iso,
          games: attachStates(applyGameOverride(s.game, overrideMap.get(s.game.slug))),
        }));
      return [...base, ...extras];
    },
    staleTime: STALE,
  });
}

export function useStates() {
  return useQuery({
    queryKey: ["states"],
    queryFn: async () => STATES.filter((s) => s.has_lottery),
    // Rendered during SSR so crawlers see the state links in the initial HTML.
    placeholderData: () => STATES.filter((s) => s.has_lottery),
    staleTime: STALE * 12,
  });
}

export function useAllStates() {
  return useQuery({
    queryKey: ["all-states"],
    queryFn: async () => STATES,
    placeholderData: () => STATES,
    staleTime: STALE * 12,
  });
}

export function useStateBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["state", slug],
    queryFn: async () => (slug ? STATES.find((s) => s.slug === slug) ?? null : null),
    placeholderData: () => (slug ? STATES.find((s) => s.slug === slug) ?? null : null),
    enabled: !!slug,
  });
}

export function useAllGames() {
  const { data: live } = useLiveLottery();
  const { data: overrides } = useQuery({
    queryKey: ["game-overrides"],
    queryFn: fetchGameOverrides,
    staleTime: 60_000,
  });
  const overrideMap = buildOverrideMap(overrides);
  const overrideVersion = getOverrideVersion(overrides);
  return useQuery({
    queryKey: [
      "all-games",
      live?.data.fetched_at ?? "static",
      overrideVersion,
    ],
    queryFn: async () =>
      [
        ...ALL_GAMES,
        ...(live?.maps.syntheticGames.map((s) => s.game) ?? []),
      ].map((g) => {
        const o = overrideMap.get(g.slug);
        const withOverride = applyGameOverride(g, o);
        const attached = attachStates(withOverride);
        const jp = JACKPOT_SNAPSHOTS.find((j) => j.game_id === g.id);
        const merged = jp ? mergeJackpot(jp, live?.maps) : null;
        const liveJp = live?.maps.jackpotByGameId[g.id];
        const fallback =
          !merged && liveJp
            ? {
                id: `jp-${g.id}`,
                game_id: g.id,
                jackpot_amount: liveJp.jackpot_amount,
                cash_value: liveJp.cash_value,
                next_draw_datetime: liveJp.next_draw_iso,
                games: g,
              }
            : null;
        const snap = merged ?? fallback;
        return { ...attached, jackpot_snapshots: snap ? [snap] : [] };
      }),
    staleTime: STALE,
  });
}

export function useMultistateGames() {
  const { data: live } = useLiveLottery();
  const { data: overrides } = useQuery({
    queryKey: ["game-overrides"],
    queryFn: fetchGameOverrides,
    staleTime: 60_000,
  });
  const overrideMap = buildOverrideMap(overrides);
  const overrideVersion = getOverrideVersion(overrides);
  return useQuery({
    queryKey: ["multistate-games", live?.data.fetched_at ?? "static", overrideVersion],
    queryFn: async () =>
      MULTISTATE_GAMES.map((g) => ({
        ...applyGameOverride(g, overrideMap.get(g.slug)),
        jackpot_snapshots: JACKPOT_SNAPSHOTS.filter((j) => j.game_id === g.id).map((j) =>
          mergeJackpot(j, live?.maps),
        ),
      })),
    staleTime: STALE,
  });
}

export function useGameBySlug(slug: string | undefined, stateId?: string) {
  const { data: live } = useLiveLottery();
  const { data: overrides } = useQuery({
    queryKey: ["game-overrides"],
    queryFn: fetchGameOverrides,
    staleTime: 60_000,
  });
  const overrideMap = buildOverrideMap(overrides);
  const overrideVersion = getOverrideVersion(overrides);
  return useQuery({
    queryKey: ["game", slug, stateId, live?.data.fetched_at ?? "static", overrideVersion],
    queryFn: async () => buildGameBySlug(slug, stateId, live?.maps, overrideMap),
    // Static (feed-free) resolution so the game page and all of its internal
    // links are present in the server-rendered HTML for crawlers.
    placeholderData: () => buildGameBySlug(slug, stateId, undefined, new Map()),
    enabled: !!slug,
  });
}

function buildGameBySlug(
  slug: string | undefined,
  stateId: string | undefined,
  liveMaps: LiveByGameMaps | undefined,
  overrideMap: Map<string, GameOverrideRow>,
) {
      if (!slug) return null;
      // State-scoped games are stored with a "{stateSlug}-" prefix but linked
      // without it (e.g. URL /new-york/numbers-midday -> stored slug
      // "new-york-numbers-midday"). Try the URL slug first, then the prefixed form.
      const state = stateId ? STATES.find((s) => s.id === stateId) : undefined;
      const candidates = state
        ? [slug, `${state.slug}-${slug}`]
        : [slug];
      let g: Game | undefined;
      for (const cand of candidates) {
        g = ALL_GAMES.find(
          (x) => x.slug === cand && (stateId ? x.state_id === stateId : x.state_id === null),
        );
        if (g) break;
      }
      if (!g) {
        for (const cand of candidates) {
          const synth = liveMaps?.syntheticGames.find(
            (s) =>
              s.game.slug === cand && (stateId ? s.game.state_id === stateId : s.game.state_id === null),
          );
          if (synth) { g = synth.game; break; }
        }
      }
      if (!g) return null;
      const withOverride = applyGameOverride(g, overrideMap.get(g.slug));
      const attached = attachStates(withOverride);
      const jp = JACKPOT_SNAPSHOTS.find((j) => j.game_id === g.id);
      let merged = jp ? mergeJackpot(jp, liveMaps) : null;
      if (!merged) {
        const liveJp = liveMaps?.jackpotByGameId[g.id];
        if (liveJp) {
          merged = {
            id: `jp-${g.id}`,
            game_id: g.id,
            jackpot_amount: liveJp.jackpot_amount,
            cash_value: liveJp.cash_value,
            next_draw_datetime: liveJp.next_draw_iso,
            games: withOverride,
          } as typeof JACKPOT_SNAPSHOTS[number];
        }
      }
      return { ...attached, jackpot_snapshots: merged };
}

export function useResults(gameId?: string, limit = 25) {
  const { data: live } = useLiveLottery();
  return useQuery({
    queryKey: ["results", gameId, limit, live?.data.fetched_at ?? "static"],
    queryFn: async () => {
      if (!gameId) return [];
      const base = getResultsForGame(gameId, limit);
      const liveLatest = live?.maps.latestByGameId[gameId];
      if (!liveLatest) return base;
      // Replace/insert the live latest as the top entry
      const filtered = base.filter((r) => r.draw_date !== liveLatest.draw_date);
      const top = {
        id: `r-${gameId}-${liveLatest.draw_date}`,
        game_id: gameId,
        draw_date: liveLatest.draw_date,
        winning_numbers: liveLatest.winning_numbers,
        bonus_numbers: liveLatest.bonus_numbers,
        multiplier: liveLatest.multiplier,
        games: base[0]?.games,
      };
      return [top, ...filtered].slice(0, limit);
    },
    // SSR fallback: static draw history, so prev/next and recent-draw links
    // are crawlable without JavaScript.
    placeholderData: () => (gameId ? getResultsForGame(gameId, limit) : []),
    staleTime: STALE,
  });
}

export function useLatestResults(limit = 20) {
  const { data: live } = useLiveLottery();
  const { data: overrides } = useQuery({
    queryKey: ["game-overrides"],
    queryFn: fetchGameOverrides,
    staleTime: 60_000,
  });
  const overrideMap = buildOverrideMap(overrides);
  const overrideVersion = getOverrideVersion(overrides);
  return useQuery({
    queryKey: ["latest-results", limit, live?.data.fetched_at ?? "static", overrideVersion],
    queryFn: async () => {
      const base = getLatestResults(limit * 2).map((r) => mergeResult(r, live?.maps));
      // Add synthetic-game results so newly-discovered games appear in the latest list
      const seen = new Set(base.map((r) => r.game_id));
      const synthResults = (live?.maps.syntheticGames ?? [])
        .filter((s) => !seen.has(s.game.id))
        .map((s) => {
          if (s.latest) {
            return {
              id: `r-${s.game.id}-${s.latest.draw_date}`,
              game_id: s.game.id,
              draw_date: s.latest.draw_date,
              winning_numbers: s.latest.winning_numbers,
              bonus_numbers: s.latest.bonus_numbers,
              multiplier: s.latest.multiplier,
              games: applyGameOverride(s.game, overrideMap.get(s.game.slug)),
            };
          }
          // Synthetic game has a jackpot but no live latest draw — synthesize one
          // so the JackpotCard still shows winning numbers.
          const gen = generateLatestResultForGame(s.game);
          if (!gen) return null;
          return { ...gen, games: applyGameOverride(s.game, overrideMap.get(s.game.slug)) };
        })
        .filter((r): r is NonNullable<typeof r> => r !== null);
      return [...base, ...synthResults]
        .sort((a, b) => b.draw_date.localeCompare(a.draw_date))
        .slice(0, limit)
        .map((r) => {
          const game = r.games as Game;
          return { ...r, games: attachStates(applyGameOverride(game, overrideMap.get(game.slug))) };
        });
    },
    // Refresh so newly drawn games appear without a manual reload.
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useStateResults(stateId: string | undefined, limit = 20) {
  const { data: live } = useLiveLottery();
  const { data: overrides } = useQuery({
    queryKey: ["game-overrides"],
    queryFn: fetchGameOverrides,
    staleTime: 60_000,
  });
  const overrideMap = buildOverrideMap(overrides);
  const overrideVersion = getOverrideVersion(overrides);
  return useQuery({
    queryKey: ["state-results", stateId, limit, live?.data.fetched_at ?? "static", overrideVersion],
    queryFn: async () => buildStateResults(stateId, limit, live?.maps, overrideMap),
    placeholderData: () => buildStateResults(stateId, limit, undefined, new Map()),
    enabled: !!stateId,
    staleTime: STALE,
  });
}

function buildStateResults(
  stateId: string | undefined,
  limit: number,
  liveMaps: LiveByGameMaps | undefined,
  overrideMap: Map<string, GameOverrideRow>,
) {
      if (!stateId) return [];
      const gameIds = new Set(
        ALL_GAMES.filter((g) => g.state_id === stateId || g.is_multistate).map((g) => g.id),
      );
      const sorted = ALL_RESULTS.filter((r) => gameIds.has(r.game_id))
        .map((r) => mergeResult(r, liveMaps))
        .sort((a, b) => b.draw_date.localeCompare(a.draw_date));
      // Dedupe: keep only the most recent draw per game.
      const seen = new Set<string>();
      const latestPerGame: typeof sorted = [];
      for (const r of sorted) {
        if (seen.has(r.game_id)) continue;
        seen.add(r.game_id);
        latestPerGame.push(r);
        if (latestPerGame.length >= limit) break;
      }
      return latestPerGame.map((r) => {
        const game = r.games as Game;
        return { ...r, games: attachStates(applyGameOverride(game, overrideMap.get(game.slug))) };
      });
}

export function useGamesForState(stateSlug: string | undefined) {
  const { data: live } = useLiveLottery();
  const { data: overrides } = useQuery({
    queryKey: ["game-overrides"],
    queryFn: fetchGameOverrides,
    staleTime: 60_000,
  });
  const overrideMap = buildOverrideMap(overrides);
  const overrideVersion = getOverrideVersion(overrides);
  return useQuery({
    queryKey: ["games-for-state", stateSlug, live?.data.fetched_at ?? "static", overrideVersion],
    queryFn: async () => buildGamesForState(stateSlug, live?.maps, overrides, overrideMap),
    // Static game list rendered during SSR so every state-game link is in the
    // initial HTML for non-JS crawlers.
    placeholderData: () => buildGamesForState(stateSlug, undefined, [], new Map()),
    enabled: !!stateSlug,
  });
}

function buildGamesForState(
  stateSlug: string | undefined,
  liveMaps: LiveByGameMaps | undefined,
  overrides: GameOverrideRow[] | undefined,
  overrideMap: Map<string, GameOverrideRow>,
) {
      if (!stateSlug) return [];
      const state = STATES.find((s) => s.slug === stateSlug);
      if (!state) return [];
      const stateGames = STATE_GAMES.filter((g) => g.state_id === state.id);
      const synthForState = (liveMaps?.syntheticGames ?? [])
        .map((s) => s.game)
        .filter((g) => g.state_id === state.id || g.is_multistate);
      // CMS overrides scoped to this state (`${state.slug}-...`). Surfaces any
      // game that only exists as a game_overrides row (with a logo/name) so
      // backend-managed games always appear on the frontend.
      const overrideGamesForState = (overrides ?? [])
        .filter((o) => o.slug.startsWith(`${state.slug}-`))
        .map((o) => gameFromOverride(o, state.id));
      const seen = new Set<string>();
      const seenSlugs = new Set<string>();
      const excluded = EXCLUDED_STATE_GAME_SLUGS[state.slug] ?? new Set<string>();
      const all = [
        ...MULTISTATE_GAMES,
        ...stateGames,
        ...synthForState,
        ...overrideGamesForState,
      ].filter((g) => {
        if (excluded.has(g.slug)) return false;
        if (seen.has(g.id) || seenSlugs.has(g.slug)) return false;
        seen.add(g.id);
        seenSlugs.add(g.slug);
        return true;
      });
      return all.map((g) => {
        const withOverride = applyGameOverride(g, overrideMap.get(g.slug));
        const staticSnaps = JACKPOT_SNAPSHOTS.filter((j) => j.game_id === g.id).map((j) =>
          mergeJackpot(j, liveMaps),
        );
        if (staticSnaps.length === 0) {
          const liveJp = liveMaps?.jackpotByGameId[g.id];
          if (liveJp) {
            staticSnaps.push({
              id: `jp-${g.id}`,
              game_id: g.id,
              jackpot_amount: liveJp.jackpot_amount,
              cash_value: liveJp.cash_value,
              next_draw_datetime: liveJp.next_draw_iso,
              games: g,
            } as typeof JACKPOT_SNAPSHOTS[number]);
          }
        }
        return {
          ...attachStates(withOverride),
          jackpot_snapshots: staticSnaps,
        };
      });
}

export function useTaxRates() {
  return useQuery({
    queryKey: ["tax-rates"],
    queryFn: async () => TAX_RATES,
    staleTime: STALE * 12,
  });
}

export function useWinnersByMonth(year: number, month: number) {
  return useQuery({
    queryKey: ["winners-by-month", year, month],
    queryFn: async () => getWinnersForMonth(year, month),
    staleTime: STALE,
  });
}